<template>
  <v-card outlined>
    <v-container class="px-1 py-1" fluid>
      <v-card-title class="pt-2 pb-0">
        {{ $t('ELE0028') }}
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="3">
            <TextFieldInput :label="$t('ELE0044')" v-model="searchCondition.userCd" />
          </v-col>
          <v-col cols="3">
            <TextFieldInput :label="$t('ELE0045')" v-model="searchCondition.userName" />
          </v-col>
          <v-col cols="3">
            <TextFieldInput :label="$t('ELE0003')" v-model="searchCondition.companyCd" />
          </v-col>
          <v-col cols="3">
            <TextFieldInput :label="$t('ELE0004')" v-model="searchCondition.companyName" />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <span class="d-flex flex-row space-between mr-4">
              <Checkbox :label="$t('ELE0022')" v-model="searchCondition.validOnly" />
              <v-spacer />
              <BtnSearch class="mt-2" @click="onClickSearch" />
            </span>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <v-card outlined>
              <v-card-title class="pt-1 pb-0">
                {{ $t('ELE0023') }}
                <v-spacer />
                <BtnAdd @click="addItem" />
              </v-card-title>
              <v-data-table :headers="headers" :items="items" dense>
                <template v-slot:[`item.action`]="{ item }">
                  <IconInquiry @click="inquiryItem(item)" left />
                  <IconEdit @click="editItem(item)" right />
                </template>
                <template v-slot:[`item.isValid`]="{ item }">
                  {{ $t('isValid.' + item.isValid) }}
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <UserDialog
        v-if="item !== null"
        :dialog.sync="dialog"
        :mode="mode"
        :userCd.sync="item.userCd"
        :userName.sync="item.userName"
        :mail.sync="item.mail"
        :companyCd.sync="item.companyCd"
        :companyName.sync="item.companyName"
        :userRoleCds.sync="userRoleCds"
        :isReceivedMail.sync="item.isReceivedMail"
        :isValid.sync="item.isValid"
        :versionNo.sync="item.versionNo"
        @change="onClickSearch()"
      />
    </v-container>
  </v-card>
</template>

<script>
import { API } from '@/plugins/api'
import { ModalMode } from '@/enum'

import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import cloneDeep from 'lodash/cloneDeep'

import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import Btn from '@/components/atoms/base/button/Btn'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import BtnSearch from '@/components/atoms/button/BtnSearch'
import BtnAdd from '@/components/atoms/button/BtnAdd'
import IconEdit from '@/components/atoms/icon/IconEdit'
import IconInquiry from '@/components/atoms/icon/IconInquiry'
import UserDialog from '@/components/organisms/master/dialog/UserDialog'

export default {
  name: 'UserForm',

  mixins: [APIUtils, Message, Loading, DateUtils],

  components: {
    TextFieldInput,
    Btn,
    Checkbox,
    BtnSearch,
    BtnAdd,
    IconEdit,
    IconInquiry,
    UserDialog,
  },

  data() {
    return {
      dialog: false,
      mode: '',
      searchCondition: {
        userCd: '',
        userName: '',
        companyCd: '',
        companyName: '',
        validOnly: true,
      },
      headers: [
        { text: '', align: 'start', divider: true, width: '90', sortable: false, value: 'action' },
        { text: this.$t('ELE0044'), divider: true, value: 'userCd' },
        { text: this.$t('ELE0045'), divider: true, value: 'userName' },
        { text: this.$t('ELE0003'), divider: true, value: 'companyCd' },
        { text: this.$t('ELE0004'), divider: true, value: 'companyName' },
        { text: this.$t('ELE0014'), divider: true, value: 'isValid' },
        { text: this.$t('ELE0029'), divider: true, value: 'itemUpdatedBy' },
        { text: this.$t('ELE0039'), divider: true, value: 'userColUpdaterName' },
        { text: this.$t('ELE0030'), divider: false, value: 'editItemUpdatedAt' },
      ],
      item: null,
      items: [],
      userRoleCds: [],
      emptyItem: {
        userCd: '',
        userName: '',
        companyCd: '',
        companyName: '',
        isValid: true,
        versionNo: 0,
      },
    }
  },

  methods: {
    addItem() {
      this.mode = ModalMode.ADD
      this.item = cloneDeep(this.emptyItem)
      this.userRoleCds = []
      this.dialog = true
    },
    async editItem(item) {
      this.mode = ModalMode.EDIT
      this.item = cloneDeep(item)
      this.userRoleCds = cloneDeep(await this.getUserRole(item))
      this.dialog = true
    },
    async inquiryItem(item) {
      this.mode = ModalMode.INQUIRY
      this.item = cloneDeep(item)
      this.userRoleCds = cloneDeep(await this.getUserRole(item))
      this.dialog = true
    },
    async onClickSearch() {
      this.startLoading()
      try {
        const params = {
          userCd: this.searchCondition.userCd,
          userName: this.searchCondition.userName,
          companyCd: this.searchCondition.companyCd,
          companyName: this.searchCondition.companyName,
          validOnly: this.searchCondition.validOnly,
        }
        this.items = []
        const elements = await API.get('/master/users', params)
        elements.forEach((element) => {
          element['editItemUpdatedAt'] = this.toLocalTime(element['itemUpdatedAt'])
          element['companyName'] = element['m_company']['companyName']
          this.items.push(element)
        })
      } catch (error) {
        this.items = []
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0058') })))
      } finally {
        this.stopLoading()
      }
    },
    async getUserRole(item) {
      this.startLoading()
      try {
        const elements = await API.get(`/master/userRoles/${item.userCd}`)
        let result = []
        for (let i = 0; i < elements.length; i++) {
          result.push(elements[i].roleCd)
        }
        return result
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0046') })))
      } finally {
        this.stopLoading()
      }
    },
  },
  mounted() {
    this.onClickSearch()
  },
}
</script>
